import "../../../App.css";
import React, { useState, useEffect } from "react";
import CenterSpinner from "../../../components/CenterSpinner";
import { restApi } from "../../../util/api"
import { error_message } from "../../../util/messages";
import { Select, Checkbox } from "antd";
import PieChart from "../charts/PieChart";
import TimeSeriesChart from "../charts/TimeSeriesChart";
import HistoChart from "../charts/HistoChart";
import { group } from "console";

// TODO
// - Histogram of days from ER to utip Date
// - include mean and standard deviation
// - include % of patients not moved to utip

const PatientTrajectory: React.FC = () => {

  const [data, setData] = useState<any[]>([])
  const [filteredData, setFilteredData] = useState<any[]>([])
  const [timeSeriesData, setTimeSeriesData] = useState<any[]>([])
  const [infectiologyMedicationsData, setInfectiologyMedicationsData] = useState<any[]>([])
  const [oncologyMedicationsData, setOncologyMedicationsData] = useState<any[]>([])
  const [microorganismOptions, setMicroorganismOptions] = useState<any[]>([])
  const [utipHistogramData, setUtipHistogramData] = useState<any[]>([])
  const [utipStats, setUtipStats] = useState<any>({ mean: 0, stdDev: 0, noUtipPercentage: 0 })
  const [ageRange, setAgeRange] = useState<any>("all")
  const [sex, setSex] = useState<any>("all")
  const [tumorType, setTumorType] = useState<any>("all")
  const [outcome, setOutcome] = useState<any>("all")
  const [microorganism, setMicroorganism] = useState<any>("all")
  const [groupTumors, setGroupTumors] = useState(false)
  const [targetKey, setTargetKey] = useState("cancer")

  const sexOptions = [ // duplicate fields from SWFormUtil SexOptions
    { label: "Todo", value: "all" },
    { label: "Niño", value: "male" },
    { label: "Niña", value: "female" },
    { label: "Indeterminado", value: "indeterminate" }
  ]

  const ageRangeOptions = [
    { label: "Todo", value: "all"},
    { label: "0-5", value: "[0,5]"},
    { label: "6-10", value: "[6,10]"},
    { label: "11-15", value: "[11,15]"},
    { label: "16-20", value: "[16,20]"},
    { label: "21+", value: "[21,200]"},
  ]

  const tumorCategoryOptions = [
    { label: "Todo", value: "all" },
    { label: "Hematológico", value: "hematological" },
    { label: "Sólido", value: "solid" },
    { label: "Nervioso central", value: "centralNervous" },
  ]

  const tumorCategories: { [key: string]: string[] } = {
    hematological: [
      "hodgkinsDisease",
      "acuteLymphocyticLeukemia",
      "myeloidLymphocyticLeukemia",
      "nonHodgkinLymphoma",
    ],
    solid: [
      "wilmsTumor",
      "hepaticTumors",
      "germCellTumors",
      "ewingsSarcoma",
      "primitiveNeuroectodermalTumor",
      "rabdomiosarcoma",
      "retinoblastoma",
      "osteosarcoma",
      "langerhansCellHystiocytosis"
    ],
    centralNervous: [
      "neuroblastoma",
      "medulumoblastoma",
      "suppratentorialPrimitiveNeuroectodermalTumors",
      "anaplasicEpendimoma",
      "lowGradeAstrocytomas",
      "highGradeAstrocytomas",
    ]
  }

  const outcomes = [
    { label: "Todo", value: "all" },
    { label: "Recuperación", value: "hasEventRecovery" },
    { label: "Sepsis", value: "hasSepsis" },
    { label: "Choque septico", value: "hasSepticShock" },
    { label: "Sangrado", value: "hasBleeding" },
    { label: "Neutropenia persistente", value: "hasPersistentNeutropenia" },
    { label: "Enterocolitis", value: "hasNeutropenicEntercolitis" },
    { label: "Infección", value: "hasNosocomialInfection" },
    { label: "Cuidados paleativos", value: "hasHospiceCare" },
    { label: "Transplante", value: "hasTransplant" },
    { label: "Muerte", value: "isDead" },
    { label: "Abandono", value: "abandoned" }
  ]

  const outcomesList = [
    "hasEventRecovery",
    "hasSepsis",
    "hasSepticShock",
    "hasBleeding",
    "hasPersistentNeutropenia",
    "hasNeutropenicEntercolitis",
    "hasNosocomialInfection", 
    "hasHospiceCare",
    "hasTransplant",
    "isDead",
    "abandoned"
  ]

  const ageRangeChangeHandler = (val: any) => {
    if (val == "all") setAgeRange(val)
    else setAgeRange(JSON.parse(val))
  }

  useEffect(() => {
    getData()
  },[])

  const getData = async () => {
    try {
      const { data } = await restApi().get<Record<"report", any[]>>(
        'report/patient-trajectory'
      )
      console.log('data', data.report)
      setData(data.report)
      
      // Extract microorganisms for filter options
      const microorganismsSet = new Set<string>()
      
      data.report.forEach((d: any) => {
        const infectiologyEvents = d.events?.filter((event: any) => event.event_type === "infectiology")
        
        infectiologyEvents?.forEach((event: any) => {
          if (event.additional_data?.infections) {
            event.additional_data.infections.forEach((inf: any) => {
              if (inf.cultures) {
                inf.cultures.forEach((culture: any) => {
                  if (culture.cropReport && culture.cropReport[1]) {
                    microorganismsSet.add(culture.cropReport[1])
                  }
                })
              }
            })
          }
        })
      })
      
      // Convert set to options array
      const microOptions = [
        { label: "Todo", value: "all" },
        ...Array.from(microorganismsSet).sort().map(micro => ({
          label: micro,
          value: micro
        }))
      ]
      
      setMicroorganismOptions(microOptions)
      
    } catch (e: any) {
      console.log('e',e)
      error_message(e.response?.data?.reason)
      Promise.reject(e)
    }
  }

  useEffect(() => {
    let filtered: any[] = []
    
    data.forEach(d => {
      // set patient age
      const birthday = new Date(d.patient_info.dob)
      const erDate = new Date(d.emergency_date)
      let age = erDate.getFullYear() - birthday.getFullYear()
      if (
        erDate.getMonth() < birthday.getMonth() ||
        (erDate.getMonth() === birthday.getMonth() && erDate.getDate() < birthday.getDate())
      ) {
        age -= 1;
      }

      const newData = {
        ...d,
        age: age,
      }

      filtered.push(newData)
    })

    //filter by age
    if (ageRange !== "all") {
      filtered = filtered.filter(d => d.age >= ageRange[0] && d.age <= ageRange[1])
    }

    //filter by sex
    if (sex !== "all") {
      filtered = filtered.filter(d => sex == d.patient_info.sex)
    }

    // filter by tumor type
    if (tumorType != "all") {
      filtered = filtered.filter(d => {
        // Find oncology event in the events array
        const oncologyEvent = d.events?.find((event: any) => event.event_type === "oncology")
        return oncologyEvent?.additional_data?.find((data: any) => {
          return tumorCategories[tumorType].includes(data.cancer)
        })
      })
    }

    // filter by microorganism
    if (microorganism !== "all") {
      filtered = filtered.filter(d => {
        // Find infectiology events in the events array
        const infectiologyEvents = d.events?.filter((event: any) => event.event_type === "infectiology")
        
        return infectiologyEvents?.some((event: any) => {
          if (event.additional_data?.infections) {
            return event.additional_data.infections.some((inf: any) => {
              if (inf.cultures) {
                return inf.cultures.some((culture: any) => {
                  return culture.cropReport && culture.cropReport[1] === microorganism
                })
              }
              return false
            })
          }
          return false
        })
      })
    }

    setFilteredData(filtered)

    // Apply outcome filter to create final filtered dataset
    if (outcome !== "all") {
      filtered = filtered.filter(d => {
        const lastEvent = d.events[d.events.length - 1]
        return lastEvent?.additional_data?.[outcome]
      })
    }

    // Create time series data - group by outcome and days from ER to outcome
    const timeSeriesMap: {[key: string]: {[key: number]: number}} = {}
    
    // Determine which outcomes to process - only include outcomes that are in the outcomes dropdown
    const availableOutcomes = outcomes.filter(o => o.value !== "all").map(o => o.value)
    const outcomesToProcess = outcome === "all" ? availableOutcomes : [outcome]
    
    // Initialize map for each outcome
    outcomesToProcess.forEach(outcomeKey => {
      timeSeriesMap[outcomeKey] = {}
    })

    filtered.forEach(d => {
      if (d.emergency_date && d.outcome_date) {
        const emergencyDate = new Date(d.emergency_date)
        const outcomeDate = new Date(d.outcome_date)
        const diffTime = outcomeDate.getTime() - emergencyDate.getTime()
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        
        if (diffDays >= 0) {
          // Check which outcomes this patient has
          const lastEvent = d.events[d.events.length - 1]
          if (lastEvent?.additional_data) {
            outcomesToProcess.forEach(outcomeKey => {
              if (lastEvent.additional_data[outcomeKey]) {
                if (!timeSeriesMap[outcomeKey][diffDays]) {
                  timeSeriesMap[outcomeKey][diffDays] = 0
                }
                timeSeriesMap[outcomeKey][diffDays]++
              }
            })
          }
        }
      }
    })

    // Convert to format expected by TimeSeriesChart
    const timeSeriesArray: any[] = []
    
    outcomesToProcess.forEach(outcomeKey => {
      const outcomeLabel = outcomes.find(o => o.value === outcomeKey)?.label || outcomeKey
      Object.entries(timeSeriesMap[outcomeKey]).forEach(([days, count]) => {
        timeSeriesArray.push({
          date: `Day ${days}`,
          value: count,
          outcome: outcomeLabel,
          days: parseInt(days)
        })
      })
    })

    setTimeSeriesData(timeSeriesArray)

    // Create infectiology medications data using the outcome-filtered dataset
    const medicationsData: any[] = []
    
    filtered.forEach(d => {
      // Find infectiology events in the events array
      const infectiologyEvents = d.events?.filter((event: any) => event.event_type === "infectiology")
      
      infectiologyEvents?.forEach((event: any) => {
        if (event.additional_data?.medications) {
          event.additional_data.medications.forEach((med: any) => {
            if (med.medicineFamily && med.medicineFamily[1]) {
              medicationsData.push({
                medicineFamily: med.medicineFamily[1]
              })
            }
          })
        }
      })
    })

    setInfectiologyMedicationsData(medicationsData)

    // Create oncology medications data using the outcome-filtered dataset
    const oncologyMedicationsData: any[] = []
    
    filtered.forEach(d => {
      // Find oncology events in the events array
      const oncologyEvents = d.events?.filter((event: any) => event.event_type === "oncology")
      
      oncologyEvents?.forEach((event: any) => {
        if (event.additional_data) {
          event.additional_data.forEach((data: any) => {
            if (data.medication) {
              data.medication.forEach((med: any) => {
                if (med.medName) {
                  oncologyMedicationsData.push({
                    medName: med.medName
                  })
                }
              })
            }
          })
        }
      })
    })

    setOncologyMedicationsData(oncologyMedicationsData)

    // Create UTIP histogram data using the outcome-filtered dataset
    const utipDaysData: number[] = []
    let totalPatients = 0
    let patientsWithUtip = 0

    filtered.forEach(d => {
      totalPatients++
      
      // Find emergency event with utip_date
      const emergencyEvent = d.events?.find((event: any) => event.event_type === "emergency")
      
      if (emergencyEvent?.additional_data?.utip_date && d.emergency_date) {
        const emergencyDate = new Date(d.emergency_date)
        const utipDate = new Date(emergencyEvent.additional_data.utip_date)
        const diffTime = utipDate.getTime() - emergencyDate.getTime()
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        
        if (diffDays >= 0) {
          utipDaysData.push(diffDays)
          patientsWithUtip++
        }
      }
    })

    // Calculate statistics
    let mean = 0
    let stdDev = 0
    const noUtipPercentage = totalPatients > 0 ? ((totalPatients - patientsWithUtip) / totalPatients * 100) : 0

    if (utipDaysData.length > 0) {
      mean = utipDaysData.reduce((sum, val) => sum + val, 0) / utipDaysData.length
      
      const variance = utipDaysData.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / utipDaysData.length
      stdDev = Math.sqrt(variance)
    }

    setUtipHistogramData(utipDaysData)
    setUtipStats({
      mean: Number(mean.toFixed(2)),
      stdDev: Number(stdDev.toFixed(2)),
      noUtipPercentage: Number(noUtipPercentage.toFixed(1))
    })
  }, [data, ageRange, sex, tumorType, outcome, microorganism])

  useEffect(() => {
    if (groupTumors)
      setTargetKey('tumorType')
    else
      setTargetKey('cancer')
  }, [groupTumors])

  return (
    <div
      style={{
        margin: "2rem auto",
        width: "calc(90% + 24px)",
        backgroundColor: "white",
        borderRadius: "0.25rem",
        padding: "1rem 1rem",
        minHeight: "22rem",
      }} >
      <h2>Estancia hospitalaria por tipo de desenlace.</h2>
      {data.length ? <>
        <div style={{ marginBottom: "1rem" }}>
          <h2>Filtros</h2>
        </div>
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          columnGap: "2rem",
          rowGap: "1rem",
          alignItems: "center",
        }}>
          <div>
            <label>Sexo:</label>
            <Select
              style={{width: 120}}
              options={sexOptions}
              onChange={setSex}
            />
          </div>
          <div>
            <label>Edad:</label>
            <Select
              style={{width: 120}}
              options={ageRangeOptions}
              onChange={ageRangeChangeHandler}
            />
          </div>
          <div>
            <label>Desenlaces:</label>
            <Select
              style={{width: 180}}
              options={outcomes}
              onChange={setOutcome}
              value={outcome}
            />
          </div>
          <div>
            <label>Microorganismo:</label>
            <Select
              style={{width: 200}}
              options={microorganismOptions}
              onChange={setMicroorganism}
              value={microorganism}
            />
          </div>
          {!groupTumors && <div>
            <label>Distribución por tipo tumoral:</label>
            <Select
              style={{width: 150}}
              options={tumorCategoryOptions}
              onChange={setTumorType}
            />
          </div>}
        </div>
        <div style={{ marginTop: "2rem" }}>
          <h3>
            {outcome === "all" 
              ? "Serie temporal de días desde fecha de emergencia hasta resultado por tipo de desenlace"
              : `Serie temporal de días desde fecha de emergencia hasta resultado - ${outcomes.find(o => o.value === outcome)?.label}`
            }
          </h3>
          {timeSeriesData.length > 0 ? (
            <TimeSeriesChart 
              data={timeSeriesData}
              dimensions={[1000, 500]}
              xLabel="Dias de estancia hospitalaria"
              yLabel="Número de pacientes"
              xKey="days"
              yKey="value"
              groupKey={outcome === "all" ? "outcome" : undefined}
            />
          ) : (
            <p>No hay datos suficientes para mostrar la serie temporal</p>
          )}
        </div>
        <div style={{ marginTop: "2rem" }}>
          <h3>Distribución de días desde urgencias hasta UTIP</h3>
          {utipHistogramData.length > 0 ? (
            <>
              <div style={{ 
                display: "flex", 
                justifyContent: "space-around", 
                marginBottom: "1rem",
                padding: "1rem",
                backgroundColor: "#f5f5f5",
                borderRadius: "0.25rem"
              }}>
                <div style={{ textAlign: "center" }}>
                  <strong>Media:</strong> {utipStats.mean} días
                </div>
                <div style={{ textAlign: "center" }}>
                  <strong>Desviación estándar:</strong> {utipStats.stdDev} días
                </div>
                <div style={{ textAlign: "center" }}>
                  <strong>Pacientes sin UTIP:</strong> {utipStats.noUtipPercentage}%
                </div>
              </div>
              <HistoChart 
                data={utipHistogramData}
                dimensions={[1000, 400]}
                xLabel="Días desde urgencias hasta UTIP"
                yLabel="Número de pacientes"
              />
            </>
          ) : (
            <p>No hay datos de transferencia a UTIP disponibles</p>
          )}
        </div>
        <div style={{ marginTop: "2rem" }}>
          <h3>Medicamentos por tipo de microorganismo</h3>
          {infectiologyMedicationsData.length > 0 ? (
            <PieChart 
              data={infectiologyMedicationsData}
              targetKey="medicineFamily"
            />
          ) : (
            <p>No hay datos de medicamentos de infectología disponibles</p>
          )}
        </div>
        <div style={{ marginTop: "2rem" }}>
          <h3>Medicamentos oncológicos</h3>
          {oncologyMedicationsData.length > 0 ? (
            <PieChart 
              data={oncologyMedicationsData}
              targetKey="medName"
            />
          ) : (
            <p>No hay datos de medicamentos de oncología disponibles</p>
          )}
        </div>
      </> : <CenterSpinner />}
    </div>
  )
};

export default PatientTrajectory;
